import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { ButtonComponent } from './../../../shared/components/custom-button/button.component';

import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, tap } from 'rxjs';
import { AddCategoryComponent } from '../../../shared/components/dialog/add-category/add-category.component';
import { AlertDialogComponent } from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { InputComponent } from '../../../shared/components/from-input/input.component';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../../shared/services/http.service';

// 0 = Student, 1 = Teacher, 2 = CSQ
type FAQAudienceType = 0 | 1 | 2;

interface CategoryDialogData {
    initialData?: CategoryResponse;
    editMode: boolean;
}

interface CategoryResponse {
    categoryName: string;
    faqCategoryId: string;
    lastUpdatedDisplay: string;
    isActive: boolean | string;
    faQs: FAQResponse[];
}

interface ApiDataResponse {
    status: boolean;
    message: string;
    data: any;
}

interface FAQResponse {
    faqId?: string;
    question: string;
    answer: string;
    faqCategoryId: string;
    lastUpdatedDisplay: string;
    isActive?: boolean;
    displayOrder?: number;
}

interface FAQ {
    id: string;
    question: string;
    answer: string;
    lastUpdatedDisplay: string;
    categoryId: string;
    displayOrder?: number;
}

interface Category {
    id: string;
    name: string;
    faqs: FAQ[];
}

interface ReorderPayload {
    faqId: string;
    displayOrder: number;
}

interface FAQSection {
    label: string;
    audienceType: FAQAudienceType;
}

@Component({
    selector: 'app-faq',
    standalone: true,
    imports: [CommonModule, FormsModule, DragDropModule, InputComponent, ButtonComponent],
    templateUrl: './faq.component.html',
    styleUrls: ['./faq.component.scss']
})
export class FAQComponent implements OnInit {
    search: string = '';
    selectedCategory: string = '';
    showAddForm: boolean = false;
    editingId: string | null = null;
    AdminMode: boolean = false;
    selectedCategoryControl = new FormControl('');
    isLoading: boolean = false;
    FAQCategoryOptions: any = [];

    activeSection: FAQAudienceType = 0;

    readonly sections: FAQSection[] = [
        { label: 'Student FAQ', audienceType: 0 },
        { label: 'Teacher FAQ', audienceType: 1 },
        { label: 'CSQ FAQ',     audienceType: 2 },
    ];

    customErrorMessages = {
        required: 'Please select an FAQ category.'
    };

    categories: Category[] = [];
    allFAQs: FAQ[] = [];
    currentCategoryFAQs: FAQ[] = [];

    newFAQ: FAQ = { id: '', question: '', answer: '', lastUpdatedDisplay: '', categoryId: '' };
    editingFAQ: FAQ = { id: '', question: '', answer: '', lastUpdatedDisplay: '', categoryId: '' };

    constructor(
        private location: Location,
        private dialog: MatDialog,
        private httpService: HttpService,
        private snackBar: MatSnackBar,
    ) {
        const role = localStorage.getItem('userRole');
        this.AdminMode = role === 'superadmin';
    }

    ngOnInit(): void {
        this.loadSection(this.activeSection);

        this.selectedCategoryControl.valueChanges.subscribe(value => {
            if (value) {
                this.selectedCategory = value;
                this.newFAQ.categoryId = value;
                this.updateCurrentCategoryFAQs();
                this.cancelAddFAQ();
                this.cancelEdit();
            } else {
                this.selectedCategory = '';
                this.currentCategoryFAQs = [];
                this.cancelAddFAQ();
                this.cancelEdit();
            }
        });
    }

    setSection(section: FAQAudienceType): void {
        if (this.activeSection === section) return;
        this.activeSection = section;
        this.resetState();
        this.loadSection(section);
    }

    private resetState(): void {
        this.selectedCategory = '';
        this.selectedCategoryControl.setValue('', { emitEvent: true });
        this.FAQCategoryOptions = [];
        this.categories = [];
        this.allFAQs = [];
        this.currentCategoryFAQs = [];
        this.cancelAddFAQ();
        this.cancelEdit();
    }

    private loadSection(section: FAQAudienceType): void {
        this.fetchCategoryDropDown(section).subscribe();
        this.fetchFAQList(section).subscribe();
    }

    get filteredFAQs(): FAQ[] {
        if (!this.selectedCategory) return [];

        let filtered = this.allFAQs.filter(faq => faq.categoryId === this.selectedCategory);

        if (this.search && this.search.trim() !== '') {
            const searchLower = this.search.toLowerCase().trim();
            filtered = filtered.filter(faq =>
                faq.question.toLowerCase().includes(searchLower) ||
                faq.answer.toLowerCase().includes(searchLower)
            );
        }

        return filtered.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    }

    get hasFAQsInSelectedCategory(): boolean {
        return this.filteredFAQs.length > 0;
    }

    private updateCurrentCategoryFAQs(): void {
        if (!this.selectedCategory) {
            this.currentCategoryFAQs = [];
            return;
        }

        let faqs = this.allFAQs.filter(faq => faq.categoryId === this.selectedCategory);

        if (this.search && this.search.trim() !== '') {
            const searchLower = this.search.toLowerCase().trim();
            faqs = faqs.filter(faq =>
                faq.question.toLowerCase().includes(searchLower) ||
                faq.answer.toLowerCase().includes(searchLower)
            );
        }

        this.currentCategoryFAQs = faqs.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    }

    goBack(): void {
        this.location.back();
    }

    updateSearch(searchValue: string): void {
        this.search = searchValue;
        this.updateCurrentCategoryFAQs();
    }

    applyFilter(): void {
        this.updateCurrentCategoryFAQs();
    }

    fetchCategoryDropDown(section: FAQAudienceType = this.activeSection) {
        return this.httpService.get<ApiDataResponse>(`faqcategories?audienceType=${section}`).pipe(
            tap({
                next: (response) => {
                    if (response.status && Array.isArray(response.data)) {
                        const categoryData = response.data as CategoryResponse[];

                        this.FAQCategoryOptions = categoryData.map(item => ({
                            label: item.categoryName,
                            value: item.faqCategoryId
                        }));

                        this.categories = categoryData.map(item => ({
                            id: item.faqCategoryId,
                            name: item.categoryName,
                            faqs: []
                        }));
                    } else {
                        this.FAQCategoryOptions = [];
                        this.categories = [];
                    }
                },
                error: (err) => {
                    this.showErrorToast(err?.message);
                    this.FAQCategoryOptions = [];
                    this.categories = [];
                }
            })
        );
    }

    fetchFAQList(section: FAQAudienceType = this.activeSection) {
        return this.httpService.get<ApiDataResponse>(`faqs/all?audienceType=${section}`).pipe(
            tap({
                next: (response) => {
                    if (response.status && Array.isArray(response.data)) {
                        const categoryData = response.data as CategoryResponse[];

                        const processedCategories = categoryData.map(category => ({
                            id: category.faqCategoryId,
                            name: category.categoryName,
                            faqs: category.faQs ? category.faQs.map((faq, index) => ({
                                id: faq.faqId || this.generateTempId(),
                                question: faq.question,
                                answer: faq.answer,
                                categoryId: category.faqCategoryId,
                                lastUpdatedDisplay: faq.lastUpdatedDisplay || category.lastUpdatedDisplay,
                                displayOrder: faq.displayOrder || index + 1
                            })) : []
                        }));

                        this.categories = this.categories.map(category => {
                            const updatedCategory = processedCategories.find(cat => cat.id === category.id);
                            return updatedCategory || category;
                        });

                        this.allFAQs = processedCategories.flatMap(category => category.faqs)
                            .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

                        if (this.selectedCategory) {
                            this.updateCurrentCategoryFAQs();
                        }
                    } else {
                        this.allFAQs = [];
                        this.currentCategoryFAQs = [];
                    }
                },
                error: (err) => {
                    this.showErrorToast(err?.message);
                    this.allFAQs = [];
                    this.currentCategoryFAQs = [];
                }
            })
        );
    }

    saveFAQ(faqData: FAQ, isEdit: boolean = false) {
        if (!this.selectedCategory) {
            this.showErrorToast('Please select a category first.');
            this.isLoading = false;
            return;
        }
        const formData = {
            question: faqData.question.trim(),
            answer: faqData.answer.trim(),
            faqCategoryId: this.selectedCategory,
            isActive: true
        };

        let apiCall;

        if (isEdit && faqData.id && !faqData.id.startsWith('temp_')) {
            apiCall = this.httpService.put<ApiDataResponse>(`faqs/${faqData.id}`, formData, true);
        } else {
            apiCall = this.httpService.post<ApiDataResponse>('faqs', formData, true);
        }

        apiCall
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (response) => {
                    if (response.status) {
                        this.showSuccessToast(response.message);
                        this.isLoading = false;
                        this.fetchFAQList().subscribe();
                        if (isEdit) {
                            this.cancelEdit();
                        } else {
                            this.cancelAddFAQ();
                        }
                    } else {
                        this.showErrorToast(response.message);
                        this.isLoading = false;
                    }
                },
                error: (err) => {
                    this.showErrorToast(err?.message);
                    this.isLoading = false;
                }
            });
    }

    getCategoryTitle(): string {
        const category = this.categories.find(cat => cat.id === this.selectedCategory);
        return category ? `FAQs in ${category.name}` : 'Select a category to view FAQs';
    }

    toggleAddFAQ(): void {
        if (!this.selectedCategory) {
            this.showErrorToast('Please select a category first.');
            return;
        }

        this.showAddForm = !this.showAddForm;
        if (this.showAddForm) {
            this.cancelEdit();
            this.newFAQ = { id: '', question: '', answer: '', lastUpdatedDisplay: '', categoryId: this.selectedCategory };
        } else {
            this.cancelAddFAQ();
        }
    }

    cancelAddFAQ(): void {
        this.showAddForm = false;
        this.newFAQ = { id: '', question: '', answer: '', lastUpdatedDisplay: '', categoryId: this.selectedCategory };
    }

    saveNewFAQ(): void {
        if (this.isLoading) return;
        if (!this.newFAQ.question.trim() || !this.newFAQ.answer.trim()) {
            this.showErrorToast('Please fill in both question and answer.');
            return;
        }
        if (!this.selectedCategory) {
            this.showErrorToast('Please select a category first.');
            return;
        }
        this.isLoading = true;
        this.newFAQ.categoryId = this.selectedCategory;
        this.saveFAQ(this.newFAQ, false);
    }

    editFAQ(faq: FAQ): void {
        this.editingId = faq.id;
        this.editingFAQ = { ...faq };
        this.showAddForm = false;
    }

    saveEdit(): void {
        if (this.isLoading) return;
        if (!this.editingFAQ.question.trim() || !this.editingFAQ.answer.trim()) {
            this.showErrorToast('Please fill in both question and answer.');
            return;
        }
        if (!this.editingId) {
            this.showErrorToast('No FAQ selected for editing.');
            return;
        }
        this.isLoading = true;
        this.saveFAQ(this.editingFAQ, true);
    }

    cancelEdit(): void {
        this.editingId = null;
        this.editingFAQ = { id: '', question: '', answer: '', lastUpdatedDisplay: '', categoryId: '' };
    }

    deleteFAQ(faq: FAQ): void {
        const dialogData = {
            title: 'Confirm delete',
            message: `Are you sure you want to delete this FAQ?`,
            okText: 'Delete',
            cancelText: 'Cancel',
            icon: 'warning',
            submessage: ''
        };

        this.dialog.open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
            .afterClosed().subscribe(result => {
                if (result && faq.id && !faq.id.startsWith('temp_')) {
                    this.httpService.delete<ApiDataResponse>(`faqs/${faq.id}`).subscribe({
                        next: (response) => {
                            if (response.status) {
                                this.showSuccessToast(response.message);
                                this.fetchFAQList().subscribe();
                            } else {
                                this.showErrorToast(response.message);
                            }
                        },
                        error: (err) => {
                            this.showErrorToast(err?.message);
                        }
                    });
                } else if (result && faq.id.startsWith('temp_')) {
                    this.allFAQs = this.allFAQs.filter(f => f.id !== faq.id);
                    this.updateCurrentCategoryFAQs();
                    this.showSuccessToast('FAQ removed successfully.');
                }
            });
    }

    moveUp(index: number): void {
        if (index > 0) {
            const curr = this.currentCategoryFAQs[index];
            const prev = this.currentCategoryFAQs[index - 1];
            const temp = curr.displayOrder;
            curr.displayOrder = prev.displayOrder;
            prev.displayOrder = temp;
            [this.currentCategoryFAQs[index], this.currentCategoryFAQs[index - 1]] =
                [this.currentCategoryFAQs[index - 1], this.currentCategoryFAQs[index]];
            this.updateAllFAQsOrder();
            this.updateFAQOrderInBackend([curr, prev]);
        }
    }

    moveDown(index: number): void {
        if (index < this.currentCategoryFAQs.length - 1) {
            const curr = this.currentCategoryFAQs[index];
            const next = this.currentCategoryFAQs[index + 1];
            const temp = curr.displayOrder;
            curr.displayOrder = next.displayOrder;
            next.displayOrder = temp;
            [this.currentCategoryFAQs[index], this.currentCategoryFAQs[index + 1]] =
                [this.currentCategoryFAQs[index + 1], this.currentCategoryFAQs[index]];
            this.updateAllFAQsOrder();
            this.updateFAQOrderInBackend([curr, next]);
        }
    }

    drop(event: CdkDragDrop<FAQ[]>): void {
        moveItemInArray(this.allFAQs, event.previousIndex, event.currentIndex);
        moveItemInArray(this.currentCategoryFAQs, event.previousIndex, event.currentIndex);
        this.currentCategoryFAQs.forEach((faq, index) => {
            faq.displayOrder = index + 1;
        });
        this.updateAllFAQsOrder();
        this.updateFAQOrder();
    }

    private updateAllFAQsOrder(): void {
        this.currentCategoryFAQs.forEach(faq => {
            const index = this.allFAQs.findIndex(f => f.id === faq.id);
            if (index !== -1) this.allFAQs[index].displayOrder = faq.displayOrder;
        });
        this.allFAQs.sort((a, b) => {
            if (a.categoryId !== b.categoryId) return a.categoryId.localeCompare(b.categoryId);
            return (a.displayOrder || 0) - (b.displayOrder || 0);
        });
    }

    private updateFAQOrderInBackend(faqsToUpdate: FAQ[]): void {
        const reorderPayload: ReorderPayload[] = faqsToUpdate
            .filter(faq => faq.id && !faq.id.startsWith('temp_'))
            .map(faq => ({ faqId: faq.id, displayOrder: faq.displayOrder || 0 }));

        if (reorderPayload.length > 0) {
            this.httpService.put<ApiDataResponse>('faqs/reorder', reorderPayload, true)
                .subscribe({
                    next: (response) => {
                        if (!response.status) this.fetchFAQList().subscribe();
                    },
                    error: () => {
                        this.fetchFAQList().subscribe();
                    }
                });
        }
    }

    private updateFAQOrder(): void {
        const reorderPayload: ReorderPayload[] = this.currentCategoryFAQs
            .filter(faq => faq.id && !faq.id.startsWith('temp_'))
            .map((faq, index) => ({ faqId: faq.id, displayOrder: index + 1 }));

        if (reorderPayload.length > 0) {
            this.httpService.put<ApiDataResponse>('faqs/reorder', reorderPayload, true)
                .subscribe({
                    next: (response) => {
                        if (!response.status) this.fetchFAQList().subscribe();
                    },
                    error: () => {
                        this.fetchFAQList().subscribe();
                    }
                });
        }
    }

    openAddCategory(): void {
        const dialogRef = this.dialog.open(AddCategoryComponent, {
            width: '34.375rem',
            maxWidth: '34.375rem',
            minWidth: '34.375rem',
            data: { initialData: undefined, EditMode: false, type: false, audienceType: this.activeSection },
            disableClose: true,
            autoFocus: false,
            panelClass: 'custom-dialog-container'
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.success) {
                this.fetchCategoryDropDown().subscribe();
                this.fetchFAQList().subscribe();
            }
        });
    }

    private generateTempId(): string {
        return 'temp_' + Math.random().toString(36).substr(2, 9);
    }

    private showSuccessToast(message: string): void {
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'success-toast']
        });
    }

    private showErrorToast(message: string | null | undefined): void {
        if (!message) return;
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'error-toast']
        });
    }
}
